import { FONT_MONO } from '../config/fonts';
import { PAGE_SX } from '../components/pageLayout';
import { useEffect, useMemo, useState } from 'react'
import { Box, Chip, IconButton, InputAdornment, TextField, Tooltip, Typography, alpha, useTheme } from '@mui/material'
import { BlockOutlined, CheckCircleOutline, Close, Search } from '@mui/icons-material'
import { DataGrid } from '@mui/x-data-grid'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import formatDistanceToNow from 'date-fns/formatDistanceToNow'

import { useAuthContext } from '../context/auth'
import { useUserContext } from '../context/user'
import { usePathContext } from '../context/path'
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import Add from '../components/users/Add'
import FlexBetween from '../components/FlexBetween'
import Header from '../components/Header'
import CustomColumnMenu from "../components/DataGridCustomColumnMenu";
import Edit from '../components/users/Edit'
import Delete from '../components/users/Delete'
import { ROLES } from '../config/roles'
import socket from '../utils/socket';
import Reset from '../components/users/Reset';
import UserAvatar from '../components/chat/UserAvatar'
import useGridStyles from '../hooks/useGridStyles';
import OnlineStatusChip from '../components/OnlineStatusChip';
import OnlineCountPills from '../components/OnlineCountPills';

const User = () => {
  const theme = useTheme();
  const { gridSx, gridBorder } = useGridStyles();

  const { auth } = useAuthContext()
  const { setTitle } = usePathContext()
  const { users, dispatch } = useUserContext()
  const [ query, setQuery ] = useState("")
  const [ notFound, setNotFound ] = useState(false)
  const [ units, setUnits ] = useState([])
  const axiosPrivate = useAxiosPrivate()
  const authRoles = auth?.roles ?? [];

  const isAdminOrRoot =
    authRoles.includes(ROLES.Admin) ||
    authRoles.includes(ROLES.Root);

  const admin = !!auth && isAdminOrRoot;
  
  const permitDeleteUser = (auth, user) => {
    const authRoles = auth?.roles ?? [];
    const userRoles = user?.roles ?? [];

    return (
      (!authRoles.includes(ROLES.Admin) && !userRoles.includes(ROLES.Root)) ||
      userRoles.includes(ROLES.User)
    );
  };

  // ---------- Theme ----------
  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.secondary.main;
  const muted = theme.palette.text.secondary;
  const line = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  // Online = status "good", offline = neutral (same validated pair as the Dashboard)
  const statusColor = isDark
    ? { online: '#43a047', offline: '#8a8fab' }
    : { online: '#2e7d32', offline: '#858585' };

  const unitName = (unitId) => units.find((unit) => unit._id === unitId)?.name || 'All';
  const renderUnit = (params) => (
    <Typography variant="body2" noWrap sx={{ color: params.value === 'All' ? muted : 'text.primary' }}>
      {params.value}
    </Typography>
  );

  const columns = [
    {
      field: "order",
      headerName: "No.",
      width: 34,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
          if (!params.api || !params.id) return null;
          const index = params.api.getAllRowIds?.().indexOf(params.id);
          return <Typography variant="body2" sx={{ color: muted }}>{index + 1}</Typography>;
      }
    },
    {
      field: "name",
      headerName: "Name",
      flex: 0.7,
      minWidth: 200,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" minWidth={0}>
          <UserAvatar selectedUser={params.row} />
          <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 0.7,
      renderCell: (params) => (
        <Typography variant="body2" noWrap sx={{ color: muted }}>{params.value}</Typography>
      ),
    },
    { field: "commandId", headerName: "Command", flex: 0.4, valueGetter: unitName, renderCell: renderUnit },
    { field: "regimentId", headerName: "Regiment", flex: 0.4, valueGetter: unitName, renderCell: renderUnit },
    { field: "companyId", headerName: "Company", flex: 0.5, valueGetter: unitName, renderCell: renderUnit },
    { field: "outpostId", headerName: "Outpost", flex: 0.5, valueGetter: unitName, renderCell: renderUnit },
    {
      field: "roles",
      headerName: "Role",
      flex: 0.35,
      renderCell: (params) => (
        <Box display="flex" gap={0.5} flexWrap="wrap">
          {[].concat(params.value ?? []).map((role) => (
            <Chip
              key={role}
              size="small"
              variant="outlined"
              label={role}
              sx={{
                height: 22,
                fontWeight: 600,
                fontSize: '0.72rem',
                // Root stands out by being filled, not by borrowing a status color
                color: role === ROLES.Root ? theme.palette.getContrastText(accent) : accent,
                borderColor: alpha(accent, 0.5),
                backgroundColor: role === ROLES.Root ? alpha(accent, 0.68) : alpha(accent, 0.08),   // Root: filled, a little transparent
              }}
            />
          ))}
        </Box>
      ),
    },
    {
      field: "deviceId",
      headerName: "Device ID",
      flex: 0.45,
      renderCell: (params) => (
        <Typography
          variant="body2"
          noWrap
          sx={{ color: params.value ? 'text.primary' : muted, fontFamily: params.value ? FONT_MONO : undefined }}
        >
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: "isOnline",
      headerName: "Online",
      flex: 0.35,
      renderCell: (params) => {
        const online = params.value === true;
        const seen = params.row.lastActive ? new Date(params.row.lastActive) : null;
        const tip = online
          ? 'Connected now'
          : seen && !Number.isNaN(seen.getTime())
          ? `Last seen ${formatDistanceToNow(seen, { addSuffix: true })}`
          : 'Never connected';

        return <OnlineStatusChip online={online} tip={tip} />;
      },
    },
    {
      field: "lastActive",
      headerName: "Last Active",
      flex: 0.6,
      renderCell: (params) => {
        const date = params.value ? new Date(params.value) : null;
        const valid = date && !Number.isNaN(date.getTime());

        return (
          <Tooltip title={valid ? date.toLocaleString() : ''} placement="top">
            <Typography variant="body2" noWrap sx={{ color: muted }}>
              {valid ? formatDistanceToNow(date, { addSuffix: true }) : '—'}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "access",
      headerName: "Status",
      flex: 0.5,
      valueGetter: (value, row) => (row.active ? 'Active' : 'Inactive'),
      renderCell: (params) => {
        const active = params.row.active;
        const color = active ? statusColor.online : statusColor.offline;
        return (
          <Chip
            size="small"
            label={active ? 'Active' : 'Inactive'}
            icon={active ? <CheckCircleOutline /> : <BlockOutlined />}
            sx={{
              height: 24,
              fontWeight: 600,
              fontSize: '0.75rem',
              color: active ? (isDark ? '#81c784' : '#1b5e20') : muted,
              backgroundColor: alpha(color, active ? 0.16 : 0.12),
              border: `1px solid ${alpha(color, 0.4)}`,
              '& .MuiChip-icon': { color: 'inherit', fontSize: 16, ml: '6px' },
            }}
          />
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      flex: 0.5,
      minWidth: 120,
      renderCell: (params) => {
        const user = users?.find((u) => u._id === params.row._id);

        if (!user) {
          return null;
        }

        return (
          <Box display="flex" gap={0}>
            {!(user.roles ?? []).includes(ROLES.Root) && <Reset user={user} />}

            {units.length > 0 && <Edit user={user} units={units}/>}

            {permitDeleteUser(auth, user) && <Delete user={user} />}
          </Box>
        );
      },
    },   
  ];

  const userStats = useMemo(() => {
    const totalUsers = users?.length || 0
    const onlineUsers = users?.filter((user) => user.isOnline).length || 0
    const offlineUsers = totalUsers - onlineUsers

    return { totalUsers, onlineUsers, offlineUsers }
  }, [users])

  useEffect(() => {
    setTitle("User Management")
    
    let isMounted = true
    const abortController = new AbortController()
    
    const getAllUser = async () => {
      if(!auth) return;
      try {
        const response = await axiosPrivate.get('/api/users', {
          signal: abortController.signal
        })
        isMounted && dispatch({type: 'SET_USER', payload: response.data})
      } catch (err) {
        // console.log(err)
        setNotFound(true)
      }
    }
    
    if(auth){
      getAllUser()
    }

    const fetchUnits = async () => {
      try {
        const res = await axiosPrivate.get('/api/units');
        setUnits(res.data)
      } catch (err) {
        console.error(err);
      }
    };

    fetchUnits()

    // socket.emit('online', auth._id)
    
    // const heartbeatInterval = setInterval(() => {
    //   if (auth?._id) {
    //     socket.emit('heartbeat', auth._id);
    //   }
    // }, 15000);

    const onList = (list) => dispatch({ type: 'SET_USER', payload: list })
    socket.on('adminUpdateUserList', onList)

    socket.on('profileUpdated', getAllUser)

    const intervalId = setInterval(getAllUser, 5000);

    return () => {
      isMounted = false
      // clearInterval(heartbeatInterval)
      clearInterval(intervalId)
      socket.off('adminUpdateUserList', onList)
      socket.off('profileUpdated', getAllUser)     // keep the handler: other components listen to this event too
      abortController.abort()
    }
  },[auth, axiosPrivate, dispatch, setTitle])

  const filteredNames = useMemo(() => {
    const lowerQuery = query.toLowerCase();

    return [...(users || [])].filter((user) =>
      user.name?.toLowerCase().includes(lowerQuery) ||
      user.email?.toLowerCase().includes(lowerQuery) ||
      user.roles?.join(", ").toLowerCase().includes(lowerQuery)
    );
  }, [users, query]);

  return (
    <>
      {admin && (
        <>
          <Box sx={PAGE_SX}>
            <FlexBetween 
              sx={{
                flexDirection: {
                  xs: 'column',
                  sm: 'row',
                },
                alignItems: {
                  xs: 'flex-start',
                  sm: 'center',
                },
                gap: '1rem',
              }}
            >
              {/* Header */}
              <Header title="USERS" subtitle="List Users and Add, Edit or Remove User" />

              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                {/* Users Count */}
                <OnlineCountPills
                  total={userStats.totalUsers}
                  online={userStats.onlineUsers}
                  offline={userStats.offlineUsers}
                  totalIcon={<PeopleAltOutlinedIcon sx={{ fontSize: 16, color: accent }} />}
                />

                {/* Search */}
                <TextField
                  size="small"
                  placeholder="Search name, email or role"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape' && query) setQuery('') }}
                  inputProps={{ 'aria-label': 'Search users' }}
                  sx={{
                    width: { xs: '100%', sm: '17rem' },
                    '& .MuiOutlinedInput-root': {
                      height: 36,
                      borderRadius: 2,
                      backgroundColor: theme.palette.background.alt,
                      '& fieldset': { borderColor: line },
                      '&:hover fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.25)' },
                      '&.Mui-focused fieldset': { borderColor: accent, borderWidth: 1 },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 20, color: muted }} />
                      </InputAdornment>
                    ),
                    // Clear (X): only while there is text; Esc does the same
                    endAdornment: query ? (
                      <InputAdornment position="end">
                        <Tooltip title="Clear search">
                          <IconButton
                            size="small"
                            edge="end"
                            aria-label="Clear search"
                            onClick={() => setQuery('')}
                            onMouseDown={(e) => e.preventDefault()}   // keep focus in the field
                            sx={{
                              p: 0.25,
                              color: muted,
                              '&:hover': { color: accent, backgroundColor: alpha(accent, 0.1) },
                            }}
                          >
                            <Close sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ) : null,
                  }}
                />

                {units.length > 0 ? (
                  <Add units={units} />
                ) : (
                  <Typography variant="body2" sx={{ color: muted }}>
                    Add units first to create users
                  </Typography>
                )}
              </Box>
            </FlexBetween>

            <Box
              mt="1rem"
              flex={1}
              minHeight="400px"
              backgroundColor={theme.palette.background.alt}
              border={gridBorder}
              borderRadius="0.55rem"
              overflow="hidden"
            >
              {/* Grid Table */}
              <DataGrid
                rowHeight={60}
                columnHeaderHeight={48}
                loading={!users}
                getRowId={(row) => row._id}
                rows={filteredNames || []}
                columns={columns}
                disableRowSelectionOnClick
                localeText={{
                  noRowsLabel: notFound ? 'Unable to load users' : 'No users yet',
                  noResultsOverlayLabel: 'No users found',
                }}
                slots={{
                  noRowsOverlay: () => (
                    <Box height="100%" display="grid" sx={{ placeItems: 'center', color: muted }}>
                      <Box textAlign="center">
                        <PeopleAltOutlinedIcon sx={{ fontSize: 40, opacity: 0.5 }} />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {query
                            ? `No users match "${query}"`
                            : notFound
                            ? 'Unable to load users'
                            : 'No users yet'}
                        </Typography>
                      </Box>
                    </Box>
                  ),
                }}
                components={{
                  ColumnMenu: CustomColumnMenu,
                }}
                sx={gridSx}
              />              
            </Box>     
          </Box>
        </>        
      )}
    </>
  )
}

export default User